import React, { useState, useEffect } from 'react'
import { Modal, Button, Spin, Icon } from 'antd'
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api'

const containerStyle = {
    width: '100%',
    height: '500px',
    borderRadius: '8px'
}

const AllLocationsMapModal = ({ visible, onCancel, stops }) => {
    const [selectedMarker, setSelectedMarker] = useState(null)
    const [map, setMap] = useState(null)
    const [center, setCenter] = useState({ lat: 0, lng: 0 })

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: ['places']
    })

    const onMapLoad = (mapInstance) => {
        setMap(mapInstance)
        if (stops && stops.length > 0) {
            const bounds = new window.google.maps.LatLngBounds()
            stops.forEach(stop => {
                const coords = getCoords(stop)
                if (coords) bounds.extend(coords)
            })
            mapInstance.fitBounds(bounds)
        }
    }

    useEffect(() => {
        // Reset selection when stops change
        setSelectedMarker(null)

        if (stops && stops.length > 0) {
            // Update bounds if map is already loaded
            if (map && window.google) {
                const bounds = new window.google.maps.LatLngBounds()
                let count = 0
                stops.forEach(stop => {
                    const coords = getCoords(stop)
                    if (coords) {
                        bounds.extend(coords)
                        count++
                    }
                })
                if (count > 0) {
                    map.fitBounds(bounds)
                }
            } else {
                // Fallback to average center if map not yet loaded
                let totalLat = 0
                let totalLng = 0
                let count = 0

                stops.forEach(stop => {
                    const coords = getCoords(stop)
                    if (coords) {
                        totalLat += coords.lat
                        totalLng += coords.lng
                        count++
                    }
                })

                if (count > 0) {
                    setCenter({ lat: totalLat / count, lng: totalLng / count })
                }
            }
        }
    }, [stops, visible, map])


    const getCoords = (stop) => {
        // Try to get coordinates from known patterns
        const coordStr = stop.base_coordinates ||
            (stop.location && stop.location.base_coordinates) ||
            stop.coordinates ||
            (stop.location && stop.location.coordinates)

        if (coordStr && typeof coordStr === 'string') {
            const parts = coordStr.split(',')
            if (parts.length === 2) {
                const lat = parseFloat(parts[0])
                const lng = parseFloat(parts[1])
                if (!isNaN(lat) && !isNaN(lng)) {
                    return { lat, lng }
                }
            }
        }

        // Fallback to numeric lat/lng
        const lat = stop.lat || (stop.location && stop.location.lat)
        const lng = stop.lng || (stop.location && stop.location.lng)
        if (typeof lat === 'number' && typeof lng === 'number') {
            return { lat, lng }
        }

        return null
    }

    const mapOptions = {
        zoomControl: true,
        streetViewControl: true,
        mapTypeControl: true,
        fullscreenControl: true,
    }

    const getMarkerIcon = () => ({
        path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z',
        fillColor: '#0068FF',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
        scale: 1.5,
        labelOrigin: { x: 0, y: -30 }
    })

    return (
        <Modal
            title="📍 Treasure Hunt Stops Location"
            visible={visible}
            onCancel={onCancel}
            footer={[
                <Button key="close" onClick={onCancel} className="rounded">
                    Close
                </Button>
            ]}
            width={850}
            centered
            destroyOnClose
            bodyStyle={{ padding: '0px' }}
        >
            {isLoaded ? (
                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={center}
                    zoom={15}
                    options={mapOptions}
                    onLoad={onMapLoad}
                >
                    {stops.map((stop, index) => {
                        const coords = getCoords(stop)
                        if (!coords) return null
                        const stopId = stop.id || stop.location_id || index
                        const key = `${stopId}-${coords.lat}-${coords.lng}`
                        return (
                            <Marker
                                key={key}
                                position={coords}
                                icon={getMarkerIcon()}
                                label={{
                                    text: (stop.stop_number || stop.number || index + 1).toString(),
                                    color: 'white',
                                    fontWeight: '900',
                                    fontSize: '14px'
                                }}
                                onClick={() => setSelectedMarker(stop)}
                            />
                        )
                    })}




                    {selectedMarker && (
                        <InfoWindow
                            position={getCoords(selectedMarker)}
                            onCloseClick={() => setSelectedMarker(null)}
                        >
                            <div style={{ padding: '8px', maxWidth: '200px' }}>
                                <h4 style={{ margin: '0 0 5px 0', borderBottom: '1px solid #f0f0f0', paddingBottom: '4px' }}>
                                    Stop #{selectedMarker.stop_number || selectedMarker.number || stops.indexOf(selectedMarker) + 1}
                                </h4>
                                <div style={{ fontSize: '12px', color: '#595959', fontWeight: 'bold' }}>
                                    {selectedMarker.location?.name}
                                </div>
                                {selectedMarker.location?.address && (
                                    <div style={{ fontSize: '12px', color: '#595959' }}>
                                        <Icon type="environment" style={{ marginRight: 4 }} />
                                        {selectedMarker.location.address}
                                    </div>
                                )}
                            </div>
                        </InfoWindow>
                    )}
                </GoogleMap>
            ) : (
                <div style={{ height: '500px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading stops on map...</div>
                </div>
            )}
        </Modal>
    )
}

export default AllLocationsMapModal
